/**
 * EngagementWipSummaryPage
 *
 * Per-engagement rollup: budget vs. scheduled vs. actual hours, plus
 * cost variance -- all real now (SSS `/schedules`+`/engagements` for
 * budget/scheduled, TRS `/post-times` filtered to chargeable client
 * entries for actual). Falls back to sample data
 * (src/data/sampleEngagementSummaryData.js) if the real SSS fetch errors
 * or returns nothing; if SSS is fine but TRS specifically is
 * unreachable, Actual Hrs/Cost Variance show "Pending TRS" and status
 * falls back to comparing SCHEDULED vs budget instead of actual vs
 * budget -- a graceful partial degradation rather than losing the whole
 * page over one of the two backends.
 *
 * Chart is deliberately capped to the top 10 (by scheduled hours) rows
 * of the CURRENT FILTER, decoupled from table pagination -- rendering
 * every engagement's bar at once (potentially hundreds) is what made the
 * chart feel sluggish/not smooth; capping it keeps rendering snappy
 * regardless of how many engagements match the filters, while the table
 * below still lets you page through everything.
 */
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, CheckCircle2, TrendingDown } from 'lucide-react';
import { useBranchContext } from '../hooks/useBranchContext.js';
import { useEngagements, useClients, useSchedules, useStaffList, usePositionRates, scheduleEngagementId, scheduleStaffId, scheduleHours } from '../hooks/useReferenceData.js';
import { useTimeEntries, timeEntryEngagementId, timeEntryUserId, timeEntryHours, isChargeableClientEntry } from '../hooks/useTrsTimeEntries.js';
import { buildRateLookup } from '../utils/positionRateLookup.js';
import { sampleEngagementSummary } from '../data/sampleEngagementSummaryData.js';
import { VarianceStatusBadge } from '../components/wip/VarianceStatusBadge.jsx';

const currency = (n) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const axisTickStyle = { fill: 'rgb(var(--c-text-muted))', fontSize: 12 };
const tooltipStyle = {
  backgroundColor: 'rgb(var(--c-bg-surface))',
  border: '1px solid rgb(var(--c-border))',
  borderRadius: 8,
  fontSize: 12,
  color: 'rgb(var(--c-text-primary))',
};

const CHART_MAX_BARS = 10;
const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const ENGAGEMENT_STATUS_LABELS = { planned: 'Planned', started: 'Started', completed: 'Completed' };

export const EngagementWipSummaryPage = () => {
  const { activeBranchId } = useBranchContext();
  const engagementsQuery = useEngagements(activeBranchId ? { branch_id: activeBranchId } : {});
  const clientsQuery = useClients();
  const staffQuery = useStaffList(activeBranchId ? { branch_id: activeBranchId } : {});
  const positionRatesQuery = usePositionRates();
  const schedulesQuery = useSchedules(activeBranchId ? { branchId: activeBranchId } : {});
  const timeEntriesQuery = useTimeEntries();

  const isLoading =
    engagementsQuery.isLoading || clientsQuery.isLoading || staffQuery.isLoading ||
    positionRatesQuery.isLoading || schedulesQuery.isLoading;
  const isError =
    engagementsQuery.isError || clientsQuery.isError || staffQuery.isError ||
    positionRatesQuery.isError || schedulesQuery.isError;
  // TRS excluded from the gates above -- see EngagementProgressingPage.jsx
  // for the same reasoning (a TRS hiccup shouldn't take down SSS-driven rows).
  const trsDataReady = !timeEntriesQuery.isError;

  const realRows = useMemo(() => {
    if (isLoading || isError) return [];
    const engagements = engagementsQuery.data ?? [];
    const clients = clientsQuery.data ?? [];
    const staff = staffQuery.data ?? [];
    const positionRates = positionRatesQuery.data ?? [];
    const schedules = schedulesQuery.data ?? [];
    const chargeableEntries = trsDataReady
      ? (timeEntriesQuery.data ?? []).filter(isChargeableClientEntry)
      : [];

    const rateFor = buildRateLookup(staff, positionRates);
    const clientById = new Map(clients.map((c) => [String(c.id), c]));

    const scheduledByEngagement = new Map();
    for (const s of schedules) {
      const engId = scheduleEngagementId(s);
      if (!engId) continue;
      scheduledByEngagement.set(engId, (scheduledByEngagement.get(engId) ?? 0) + scheduleHours(s));
    }

    const actualHoursByEngagement = new Map();
    const costVarianceByEngagement = new Map();
    for (const t of chargeableEntries) {
      const engId = timeEntryEngagementId(t);
      if (!engId) continue;
      const hours = timeEntryHours(t);
      const rate = rateFor(timeEntryUserId(t));
      actualHoursByEngagement.set(engId, (actualHoursByEngagement.get(engId) ?? 0) + hours);
      costVarianceByEngagement.set(engId, (costVarianceByEngagement.get(engId) ?? 0) + hours * rate);
    }
    // costVariance so far only holds actual-side charges; subtract the
    // scheduled-side charges below so it ends up as (actual - scheduled) x rate,
    // matching the same hourVariance x rate business rule used elsewhere.
    for (const s of schedules) {
      const engId = scheduleEngagementId(s);
      if (!engId) continue;
      const rate = rateFor(scheduleStaffId(s));
      costVarianceByEngagement.set(engId, (costVarianceByEngagement.get(engId) ?? 0) - scheduleHours(s) * rate);
    }

    return engagements.map((e) => {
      const id = String(e.id);
      const scheduledHours = Math.round((scheduledByEngagement.get(id) ?? 0) * 100) / 100;
      const actualHours = Math.round((actualHoursByEngagement.get(id) ?? 0) * 100) / 100;
      return {
        engagementId: id,
        engagementName: e.title ?? e.name ?? `Engagement #${id}`,
        clientName: clientById.get(String(e.client_id))?.name ?? 'Unknown Client',
        // Engagement's own attributes -- field names best-guess, same
        // caveat as elsewhere in this app (unconfirmed against a live
        // response). Used by the Division/Status filters below.
        division: e.division ?? null,
        engagementStatus: e.status ?? null,
        budgetHours: Number(e.budget_hours ?? 0),
        scheduledHours,
        actualHours,
        costVariance: Math.round((costVarianceByEngagement.get(id) ?? 0) * 100) / 100,
      };
    });
  }, [
    isLoading, isError, trsDataReady, engagementsQuery.data, clientsQuery.data, staffQuery.data,
    positionRatesQuery.data, schedulesQuery.data, timeEntriesQuery.data,
  ]);

  const usingSampleData = !isLoading && (isError || realRows.length === 0);
  const allRows = usingSampleData ? sampleEngagementSummary : realRows;
  // Sample rows always carry actualHours/costVariance; real rows do too
  // now, UNLESS TRS specifically is down while SSS is fine.
  const hasActualData = usingSampleData || trsDataReady;

  const scheduleStatus = (r) => {
    if (r.budgetHours <= 0) return 'on_track';
    if (r.scheduledHours > r.budgetHours) return 'over_budget';
    if (r.scheduledHours < r.budgetHours * 0.8) return 'under_budget';
    return 'on_track';
  };
  const actualStatus = (r) => {
    if (r.budgetHours <= 0) return 'on_track';
    if (r.actualHours > r.budgetHours) return 'over_budget';
    if (r.actualHours < r.budgetHours * 0.8) return 'under_budget';
    return 'on_track';
  };
  const statusOf = (r) => (hasActualData ? actualStatus(r) : scheduleStatus(r));

  // Filters
  const [divisionFilter, setDivisionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[0]);

  const divisionOptions = useMemo(
    () => [...new Set(allRows.map((r) => r.division).filter(Boolean))].sort(),
    [allRows]
  );
  const statusOptions = useMemo(
    () => [...new Set(allRows.map((r) => r.engagementStatus).filter(Boolean))].sort(),
    [allRows]
  );

  const filteredRows = useMemo(
    () =>
      allRows.filter(
        (r) =>
          (!divisionFilter || r.division === divisionFilter) &&
          (!statusFilter || r.engagementStatus === statusFilter)
      ),
    [allRows, divisionFilter, statusFilter]
  );

  const overBudgetCount = filteredRows.filter((r) => statusOf(r) === 'over_budget').length;
  const underBudgetCount = filteredRows.filter((r) => statusOf(r) === 'under_budget').length;
  const onTrackCount = filteredRows.filter((r) => statusOf(r) === 'on_track').length;

  const chartData = useMemo(
    () =>
      [...filteredRows]
        .sort((a, b) => b.scheduledHours - a.scheduledHours)
        .slice(0, CHART_MAX_BARS)
        .map((r) => ({
          name: r.engagementName.length > 18 ? `${r.engagementName.slice(0, 18)}\u2026` : r.engagementName,
          budgeted: r.budgetHours,
          actual: r.actualHours,
          scheduled: r.scheduledHours,
        })),
    [filteredRows]
  );

  const total = filteredRows.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  // Derived rather than synced via an effect -- if data/filters shrink
  // and `page` overflows, this just clamps for the current render
  // instead of needing a setState-in-effect to correct it.
  const clampedPage = Math.min(page, lastPage);
  const pageRows = useMemo(
    () => filteredRows.slice((clampedPage - 1) * perPage, clampedPage * perPage),
    [filteredRows, clampedPage, perPage]
  );

  const resetToFirstPage = () => setPage(1);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-primary">Engagement WIP Summary</h1>
          <p className="text-sm text-muted">
            Budget vs. scheduled vs. actual hours per engagement (SSS + TRS).
          </p>
        </div>
        {usingSampleData ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">
            Sample data{isError ? ' \u2014 live fetch failed' : ' \u2014 no live engagements for this filter'}
          </span>
        ) : (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
            Live from SSS{trsDataReady ? ' & TRS' : ' \u2014 TRS unreachable, Actual Hrs pending'}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-edge bg-surface p-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {ENGAGEMENT_STATUS_LABELS[s] ?? s}
            </option>
          ))}
        </select>

        <select
          value={divisionFilter}
          onChange={(e) => {
            setDivisionFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Divisions</option>
          {divisionOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        {(statusFilter || divisionFilter) && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter('');
              setDivisionFilter('');
              resetToFirstPage();
            }}
            className="text-sm text-muted hover:text-primary"
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-sm text-muted">{total} engagement{total === 1 ? '' : 's'}</span>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <div>
            <p className="text-xs text-muted">Over Budget</p>
            <p className="text-xl font-semibold text-primary">{overBudgetCount}</p>
          </div>
        </div>
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <div>
            <p className="text-xs text-muted">On Track</p>
            <p className="text-xl font-semibold text-primary">{onTrackCount}</p>
          </div>
        </div>
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <TrendingDown className="h-5 w-5 text-sky-500" />
          <div>
            <p className="text-xs text-muted">Under Budget</p>
            <p className="text-xl font-semibold text-primary">{underBudgetCount}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-edge bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-medium text-primary">
            Budgeted vs. {hasActualData ? 'Actual' : 'Scheduled'} Hours
          </p>
          {total > CHART_MAX_BARS && (
            <span className="text-xs text-muted">Top {CHART_MAX_BARS} by scheduled hours</span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--c-border))" vertical={false} />
            <XAxis dataKey="name" tick={axisTickStyle} axisLine={{ stroke: 'rgb(var(--c-border))' }} tickLine={false} interval={0} angle={-20} textAnchor="end" height={60} />
            <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgb(var(--c-bg-surface-hover))' }} isAnimationActive={false} />
            <Bar dataKey="budgeted" name="Budgeted Hrs" fill="#0284c7" radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey={hasActualData ? 'actual' : 'scheduled'} name={hasActualData ? 'Actual Hrs' : 'Scheduled Hrs'} fill="#fbbf24" radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto rounded-lg border border-edge bg-surface">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-edge text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3 font-medium">Engagement</th>
              <th className="px-4 py-3 font-medium">Client</th>
              <th className="px-4 py-3 font-medium">Budget Hrs</th>
              <th className="px-4 py-3 font-medium">Scheduled Hrs</th>
              <th className="px-4 py-3 font-medium">Actual Hrs</th>
              <th className="px-4 py-3 font-medium">Cost Variance</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.engagementId} className="border-b border-edge/60 text-primary hover:bg-surface-hover">
                <td className="px-4 py-3">{r.engagementName}</td>
                <td className="px-4 py-3 text-muted">{r.clientName}</td>
                <td className="px-4 py-3">{r.budgetHours.toLocaleString()}</td>
                <td className="px-4 py-3">{r.scheduledHours.toLocaleString()}</td>
                {hasActualData ? (
                  <>
                    <td className="px-4 py-3">{r.actualHours.toLocaleString()}</td>
                    <td className={`px-4 py-3 font-medium ${r.costVariance >= 0 ? 'text-amber-500' : 'text-sky-500'}`}>
                      {currency(r.costVariance)}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 text-muted">Pending TRS</td>
                    <td className="px-4 py-3 text-muted">&mdash;</td>
                  </>
                )}
                <td className="px-4 py-3">
                  <VarianceStatusBadge status={statusOf(r)} />
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted">
                  No engagements match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                resetToFirstPage();
              }}
              className="rounded-md border border-edge bg-base px-2 py-1 text-sm text-primary focus:border-accent focus:outline-none"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Showing page {clampedPage} of {lastPage} ({total} rows)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={clampedPage <= 1}
                onClick={() => setPage(Math.max(1, clampedPage - 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={clampedPage >= lastPage}
                onClick={() => setPage(Math.min(lastPage, clampedPage + 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
